/**
 * Constraint glyph placement math (Faz 6.7, OCS glyph cache parity).
 *
 * Each constraint renders as a screen-space pill anchored at its primary
 * ref point; constraints sharing one anchor fan out along the horizontal
 * axis (OCS constraint_glyph_offsets). GLYPH_SIZE mirrors OCS's 14px badge.
 */

import type { Entity } from '../../state/store';
import type { Pt3 } from '../commands/geometryMath';
import { resolveMarkerPoint } from './attach';
import { KIND_GLYPH, isDimensionalKind } from './kinds';
import type { ConstraintSet } from './types';

export const GLYPH_SIZE = 14;
const GLYPH_GAP = 6;
const GLYPH_PAD = 7;

export interface GlyphPlacement {
    constraintId: string;
    kind: string;
    label: string;
    anchor: Pt3;
    /** Fan-out offset in screen px from the shared anchor position. */
    offsetX: number;
    conflict: boolean;
    hidden: boolean;
    /** Tooltip text (kind label + measured driving value). */
    tooltip: string;
}

/** Anchor world point of one constraint (primary ref, centroid fallback). */
function anchorOf(entities: Record<string, Entity>, refs: { entityId: string; marker: number }[]): Pt3 | null {
    for (const r of refs) {
        const p = resolveMarkerPoint(entities[r.entityId], r.marker);
        if (p) return p;
    }
    return null;
}

/**
 * Compute every visible constraint's glyph placement. Pure: the layer maps
 * anchors to screen space each frame.
 */
export function computeGlyphPlacements(
    entities: Record<string, Entity>,
    set: ConstraintSet
): GlyphPlacement[] {
    interface Entry { placement: GlyphPlacement; anchorKey: string; }
    const entries: Entry[] = [];

    for (const c of set.constraints) {
        const anchor = anchorOf(entities, c.refs);
        if (!anchor) continue;
        const label = KIND_GLYPH[c.kind] ?? '?';
        const conflict = set.conflicts.includes(c.id);
        const valueText = c.value !== undefined
            ? ` ${c.kind === 'Angle' ? `${Number(c.value).toFixed(2)}°` : Number(c.value).toFixed(2)}`
            : '';
        entries.push({
            anchorKey: `${anchor[0].toFixed(6)}:${anchor[1].toFixed(6)}`,
            placement: {
                constraintId: c.id,
                kind: c.kind,
                label: isDimensionalKind(c.kind) ? `${label}${valueText}` : label,
                anchor,
                offsetX: 0,
                conflict,
                hidden: !!c.hidden,
                tooltip: `${c.kind}${valueText}`,
            },
        });
    }

    // Fan out shared anchors (OCS constraint_glyph_offsets): same-anchor
    // glyphs step sideways by pill width + gap.
    const groups = new Map<string, Entry[]>();
    for (const e of entries) {
        const list = groups.get(e.anchorKey) ?? [];
        list.push(e);
        groups.set(e.anchorKey, list);
    }
    const pillWidth = GLYPH_SIZE + GLYPH_PAD * 2;
    groups.forEach((list) => {
        if (list.length <= 1) return;
        list.sort((a, b) => a.placement.constraintId.localeCompare(b.placement.constraintId));
        list.forEach((e, i) => {
            e.placement.offsetX = (i - (list.length - 1) / 2) * (pillWidth + GLYPH_GAP);
        });
    });

    return entries.map((e) => e.placement);
}
